<template>
  <div>
    <v-container fluid>
    <!-- Key Metrics Cards -->
    <v-row class="mb-6">
      <v-col cols="4">
        <v-card class="pa-4" elevation="2">
          <div class="d-flex align-center">
            <div class="mr-4">
              <v-icon size="40" color="primary">mdi-ferry</v-icon>
            </div>
            <div>
              <div class="text-h4 font-weight-bold primary--text">{{ (metrics && metrics.totalPortCalls) || 0 }}</div>
              <div class="text-subtitle-2 grey--text">Port Calls</div>
            </div>
          </div>
        </v-card>
      </v-col>
      
      <v-col cols="4">
        <v-card class="pa-4" elevation="2">
          <div class="d-flex align-center">
            <div class="mr-4">
              <v-icon size="40" color="success">mdi-account-group</v-icon>
            </div>
            <div>
              <div class="text-h4 font-weight-bold success--text">{{ (metrics && metrics.activeClients) || 0 }}</div>
              <div class="text-subtitle-2 grey--text">Active Clients</div>
            </div>
          </div>
        </v-card>
      </v-col>
      
      <v-col cols="4">
        <v-card class="pa-4" elevation="2">
          <div class="d-flex align-center">
            <div class="mr-4">
              <v-icon size="40" color="info">mdi-ship-wheel</v-icon>
            </div>
            <div>
              <div class="text-h4 font-weight-bold info--text">{{ (metrics && metrics.totalVessels) || 0 }}</div>
              <div class="text-subtitle-2 grey--text">Active Vessels</div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Today's Jobs Section -->
    <v-row>
      <v-col cols="12">
        <v-card class="pa-4" elevation="2">
          <div class="d-flex justify-space-between align-center mb-4">
            <h3 class="text-h6 font-weight-bold">Today's Jobs</h3>
            <v-chip color="warning" small>{{ (todaysJobs && todaysJobs.length) || 0 }} Jobs</v-chip>
          </div>
          
          <div v-if="loading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <div class="mt-2 text-caption">Loading today's jobs...</div>
          </div>
          
          <div v-else-if="!todaysJobs || todaysJobs.length === 0" class="text-center py-8">
            <v-icon size="48" color="grey lighten-1">mdi-calendar-blank</v-icon>
            <div class="mt-2 text-h6 grey--text">No jobs scheduled for today</div>
            <div class="text-caption grey--text">All clear! No open operations with today's ETD.</div>
          </div>
          
          <v-data-table
            v-else
            :headers="jobHeaders"
            :items="todaysJobs"
            :items-per-page="10"
            hide-default-footer
            class="elevation-0"
          >
            <template v-slot:item.job_name="{ item }">
              <v-btn text color="primary" @click="viewJob(item)">
                <span class="font-weight-medium">{{ item.job_name }}</span>
              </v-btn>
            </template>
            <template v-slot:item.vessel_name="{ item }">
              <div class="d-flex align-center">
                <v-icon size="16" color="info" class="mr-2">mdi-ferry</v-icon>
                <span>{{ item.vessel_name }}</span>
              </div>
            </template>
            <template v-slot:item.customer_name="{ item }">
              <div class="d-flex align-center">
                <v-icon size="16" color="success" class="mr-2">mdi-account</v-icon>
                <span>{{ item.customer_name }}</span>
              </div>
            </template>
            <template v-slot:item.job_scope="{ item }">
              <div class="d-flex align-center">
                <v-icon size="16" color="warning" class="mr-2">mdi-briefcase</v-icon>
                <span>{{ item.job_scope }}</span>
              </div>
            </template>
            <template v-slot:item.incharge="{ item }">
              <div class="d-flex align-center">
                <v-icon size="16" color="info" class="mr-2">mdi-account-tie</v-icon>
                <span>{{ item.incharge }}</span>
              </div>
            </template>
            <template v-slot:item.etd="{ item }">
              <v-chip color="warning" small text-color="white">
                <v-icon left size="16">mdi-clock-outline</v-icon>
                {{ item.etd }}
              </v-chip>
            </template>
            <template v-slot:item.eta="{ item }">
              <v-chip color="info" small text-color="white">
                <v-icon left size="16">mdi-clock</v-icon>
                {{ item.eta }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Awaiting Completion Section -->
    <v-row>
      <v-col cols="12">
        <v-card class="pa-4" elevation="2">
          <div class="d-flex justify-space-between align-center mb-4">
            <h3 class="text-h6 font-weight-bold">Awaiting Completion</h3>
            <v-chip color="error" small>{{ (awaitingJobs && awaitingJobs.length) || 0 }} Overdue</v-chip>
          </div>
          
          <div v-if="loading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <div class="mt-2 text-caption">Loading awaiting completion jobs...</div>
          </div>
          
          <div v-else-if="!awaitingJobs || awaitingJobs.length === 0" class="text-center py-8">
            <v-icon size="48" color="success lighten-1">mdi-check-circle</v-icon>
            <div class="mt-2 text-h6 grey--text">No overdue jobs</div>
            <div class="text-caption grey--text">Great! All operations are completed on time.</div>
          </div>
          
          <v-data-table
            v-else
            :headers="awaitingJobHeaders"
            :items="awaitingJobs"
            :items-per-page="10"
            hide-default-footer
            class="elevation-0"
          >
            <template v-slot:item.job_name="{ item }">
              <v-btn text color="primary" @click="viewJob(item)">
                <span class="font-weight-medium">{{ item.job_name }}</span>
              </v-btn>
            </template>
            <template v-slot:item.vessel_name="{ item }">
              <div class="d-flex align-center">
                <v-icon size="16" color="info" class="mr-2">mdi-ferry</v-icon>
                <span>{{ item.vessel_name }}</span>
              </div>
            </template>
            <template v-slot:item.customer_name="{ item }">
              <div class="d-flex align-center">
                <v-icon size="16" color="success" class="mr-2">mdi-account</v-icon>
                <span>{{ item.customer_name }}</span>
              </div>
            </template>
            <template v-slot:item.job_scope="{ item }">
              <div class="d-flex align-center">
                <v-icon size="16" color="warning" class="mr-2">mdi-briefcase</v-icon>
                <span>{{ item.job_scope }}</span>
              </div>
            </template>
            <template v-slot:item.incharge="{ item }">
              <div class="d-flex align-center">
                <v-icon size="16" color="info" class="mr-2">mdi-account-tie</v-icon>
                <span>{{ item.incharge }}</span>
              </div>
            </template>
            <template v-slot:item.etd="{ item }">
              <v-chip color="error" small text-color="white">
                <v-icon left size="16">mdi-clock-alert</v-icon>
                {{ item.etd }}
              </v-chip>
            </template>
            <template v-slot:item.days_overdue="{ item }">
              <v-chip :color="item.days_overdue > 7 ? 'error' : 'warning'" small text-color="white">
                <v-icon left size="16">mdi-alert</v-icon>
                {{ item.days_overdue }} days overdue
              </v-chip>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

        <!-- Top Services and Vessels by Client Section -->
    <v-row>
      <!-- Top Services Section -->
      <v-col cols="12" md="6">
        <v-card class="pa-4" elevation="2">
          <div class="d-flex justify-space-between align-center mb-4">
            <h3 class="text-h6 font-weight-bold">Top Services</h3>
            <v-chip color="primary" small>{{ (topServices && topServices.length) || 0 }} Services</v-chip>
          </div>

          <div v-if="loading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <div class="mt-2 text-caption">Loading top services...</div>
          </div>

          <div v-else-if="!topServices || topServices.length === 0" class="text-center py-8">
            <v-icon size="48" color="grey lighten-1">mdi-cog</v-icon>
            <div class="mt-2 text-h6 grey--text">No services found</div>
            <div class="text-caption grey--text">No services have been used in operations yet.</div>
          </div>

          <v-data-table
            v-else
            :headers="topServicesHeaders"
            :items="topServices"
            :items-per-page="10"
            hide-default-footer
            class="elevation-0"
          >
            <template v-slot:item.name="{ item }">
              <div class="d-flex align-center">
                <v-icon size="16" color="primary" class="mr-2">mdi-cog</v-icon>
                <span class="font-weight-medium">{{ item.name }}</span>
              </div>
            </template>
            <template v-slot:item.usage_count="{ item }">
              <v-chip color="success" small text-color="white">
                <v-icon left size="16">mdi-chart-line</v-icon>
                {{ item.usage_count }} port calls
              </v-chip>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <!-- Vessels By Client Section -->
      <v-col cols="12" md="6">
        <v-card class="pa-4" elevation="2">
          <div class="d-flex justify-space-between align-center mb-4">
            <h3 class="text-h6 font-weight-bold">Vessels By Client</h3>
            <v-chip color="info" small>{{ (vesselsByClient && vesselsByClient.length) || 0 }} Clients</v-chip>
          </div>

          <div v-if="loading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <div class="mt-2 text-caption">Loading vessels by client...</div>
          </div>

          <div v-else-if="!vesselsByClient || vesselsByClient.length === 0" class="text-center py-8">
            <v-icon size="48" color="grey lighten-1">mdi-account-group</v-icon>
            <div class="mt-2 text-h6 grey--text">No clients found</div>
            <div class="text-caption grey--text">No clients have active vessels or port calls in the selected period.</div>
          </div>

          <v-data-table
            v-else
            :headers="vesselsByClientHeaders"
            :items="vesselsByClient"
            :items-per-page="10"
            hide-default-footer
            class="elevation-0"
          >
            <template v-slot:item.name="{ item }">
              <div class="d-flex align-center">
                <v-icon size="16" color="info" class="mr-2">mdi-account</v-icon>
                <span class="font-weight-medium">{{ item.name }}</span>
              </div>
            </template>
            <template v-slot:item.vessel_count="{ item }">
              <v-chip color="primary" small text-color="white">
                <v-icon left size="16">mdi-ferry</v-icon>
                {{ item.vessel_count }} vessels
              </v-chip>
            </template>
            <template v-slot:item.port_calls_count="{ item }">
              <v-chip color="success" small text-color="white">
                <v-icon left size="16">mdi-anchor</v-icon>
                {{ item.port_calls_count }} port calls
              </v-chip>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>

  <!-- Floating Filter Button -->
  <v-btn
    fab
    dark
    fixed
    bottom
    right
    color="primary"
    elevation="8"
    @click="showFilterDialog = true"
    class="filter-fab"
  >
    <v-icon>mdi-filter</v-icon>
  </v-btn>

  <!-- Date Range Filter Dialog -->
  <v-dialog
    v-model="showFilterDialog"
    max-width="400px"
    persistent
  >
    <v-card>
      <v-card-title class="text-h5 primary--text">
        <v-icon left color="primary">mdi-calendar-range</v-icon>
        Filter by Date Range
      </v-card-title>
      
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateRangeText"
                  label="Select Date Range"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="dateRange"
                range
                scrollable
                @input="onDateRangeChange"
              >
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="dateMenu = false">Close</v-btn>
              </v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
        
        <v-alert
          v-if="dateRangeText"
          type="info"
          outlined
          dense
          class="mt-2"
        >
          <div class="text-caption">
            <strong>Filtering by:</strong> {{ dateRangeText }}
          </div>
          <div class="text-caption">
            This will filter Key Metrics, Top Services, and Vessels by Client sections.
          </div>
        </v-alert>
      </v-card-text>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          text
          color="grey"
          @click="showFilterDialog = false"
        >
          Close
        </v-btn>
        <v-btn
          color="primary"
          @click="applyDateFilter"
          :disabled="!dateRangeText"
        >
          Apply Filter
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 's-dashboard',
  data() {
    return {
      loading: false,
      metrics: {
        totalPortCalls: 0,
        activeClients: 0,
        totalVessels: 0
      },
      todaysJobs: [],
      awaitingJobs: [],
      topServices: [],
      vesselsByClient: [],
      showFilterDialog: false,
      dateMenu: false,
      dateRange: null,
      currentDateFilter: null,
      jobHeaders: [
        { text: 'Job Name', value: 'job_name', align: 'start' },
        { text: 'Vessel', value: 'vessel_name', align: 'start' },
        { text: 'Customer', value: 'customer_name', align: 'start' },
        { text: 'Job Scope', value: 'job_scope', align: 'start' },
        { text: 'Incharge', value: 'incharge', align: 'start' },
        { text: 'ETA (Date & Time)', value: 'eta', align: 'center' },
        { text: 'ETD (Date & Time)', value: 'etd', align: 'center' }
        
      ],
      awaitingJobHeaders: [
        { text: 'Job Name', value: 'job_name', align: 'start' },
        { text: 'Vessel', value: 'vessel_name', align: 'start' },
        { text: 'Customer', value: 'customer_name', align: 'start' },
        { text: 'Job Scope', value: 'job_scope', align: 'start' },
        { text: 'Incharge', value: 'incharge', align: 'start' },
        { text: 'ETD (Date & Time)', value: 'etd', align: 'center' },
        { text: 'Days Overdue', value: 'days_overdue', align: 'center' }
      ],
      topServicesHeaders: [
        { text: 'Service Name', value: 'name', align: 'start' },
        { text: 'Usage Count', value: 'usage_count', align: 'center' }
      ],
      vesselsByClientHeaders: [
        { text: 'Client Name', value: 'name', align: 'start' },
        { text: 'Active Vessels', value: 'vessel_count', align: 'center' },
        { text: 'Port Calls', value: 'port_calls_count', align: 'center' }
      ]
    };
  },
  computed: {
    dateRangeText() {
      if (!this.dateRange || this.dateRange.length !== 2) return '';
      return this.dateRange.map((d) => moment(d, 'YYYY-MM-DD').format('DD-MM-YYYY')).join(' ~ ');
    }
  },
  methods: {
    async refreshData() {
      this.loading = true;
      try {
        // Load metrics
        try {
          await this.loadMetrics();
        } catch (error) {
          console.error('Failed to load metrics:', error);
        }

        // Load today's jobs
        try {
          await this.loadTodaysJobs();
        } catch (error) {
          console.error('Failed to load today\'s jobs:', error);
        }

        // Load awaiting completion jobs
        try {
          await this.loadAwaitingJobs();
        } catch (error) {
          console.error('Failed to load awaiting jobs:', error);
        }

        // Load top services
        try {
          await this.loadTopServices();
        } catch (error) {
          console.error('Failed to load top services:', error);
        }

        // Load vessels by client
        try {
          await this.loadVesselsByClient();
        } catch (error) {
          console.error('Failed to load vessels by client:', error);
        }
      } catch (error) {
        console.error('Dashboard error:', error);
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: `Failed to load dashboard data: ${error.message}`,
        });
      } finally {
        this.loading = false;
      }
    },
    
    async loadMetrics() {
      try {
        const params = this.currentDateFilter ? { ...this.currentDateFilter } : {};
        const response = await this.$api.dashboard.getMetrics(params);
        this.metrics = response.metrics;
      } catch (error) {
        console.error('Load metrics error:', error);
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: `Failed to load metrics: ${error.message}`,
        });
      }
    },

    async loadTodaysJobs() {
      try {
        const response = await this.$api.dashboard.getTodaysJobs();
        this.todaysJobs = response.jobs;
      } catch (error) {
        console.error('Failed to load recent jobs:', error);
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: `Failed to load recent jobs: ${error.message}`,
        });
      }
    },

    async loadAwaitingJobs() {
      try {
        const response = await this.$api.dashboard.getAwaitingCompletion();
        this.awaitingJobs = response.jobs;
      } catch (error) {
        console.error('Failed to load awaiting jobs:', error);
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: `Failed to load awaiting jobs: ${error.message}`,
        });
      }
    },

    async loadTopServices() {
      try {
        const params = this.currentDateFilter ? { ...this.currentDateFilter } : {};
        const response = await this.$api.dashboard.getTopServices(params);
        this.topServices = response.services;
      } catch (error) {
        console.error('Failed to load top services:', error);
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: `Failed to load top services: ${error.message}`,
        });
      }
    },

    async loadVesselsByClient() {
      try {
        const params = this.currentDateFilter ? { ...this.currentDateFilter } : {};
        const response = await this.$api.dashboard.getVesselsByClient(params);
        this.vesselsByClient = response.clients;
      } catch (error) {
        console.error('Failed to load vessels by client:', error);
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: `Failed to load vessels by client: ${error.message}`,
        });
      }
    },
    viewJob(job) {
      this.$router.push({ name: 'operations', query: { operationId: job.id } });
    },

    // Date filter methods
    onDateRangeChange() {
      if (this.dateRange && this.dateRange.length === 2) {
        this.dateMenu = false;
      }
    },

    clearDateFilter() {
      this.dateRange = null;
      this.currentDateFilter = null;
      this.showFilterDialog = false;
      this.refreshData();
    },

    async applyDateFilter() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: 'Please select a valid date range',
        });
        return;
      }

      this.currentDateFilter = {
        start_date: this.dateRange[0],
        end_date: this.dateRange[1]
      };
      
      this.showFilterDialog = false;
      await this.refreshData();
    },

    // Set default date range to last 30 days
    setDefaultDateRange() {
      const today = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 30);
      
      this.dateRange = [
        thirtyDaysAgo.toISOString().split('T')[0],
        today.toISOString().split('T')[0]
      ];
      this.currentDateFilter = {
        start_date: this.dateRange[0],
        end_date: this.dateRange[1]
      };
    }
  },
  async created() {
    if (!this.$root.hasAccess('')) {
      this.$store.dispatch('setAlert', {
        status: 'error',
        show: true,
        message: "Please contact admin to access this page",
      });
      this.$router.push({name: 'home'});
    } else {
      // Set default date range to last 30 days
      this.setDefaultDateRange();
      await this.refreshData();
    }
  }
};
</script>

<style scoped>
.chart-container {
  position: relative;
  width: 100%;
}

.v-data-table ::v-deep .v-data-table__wrapper {
  border-radius: 8px;
}

.v-card {
  border-radius: 12px;
}

.filter-fab {
  z-index: 1000;
  margin-bottom: 20px;
  margin-right: 20px;
}

/* Mobile responsive adjustments for metrics cards */
@media (max-width: 600px) {
  .v-col {
    padding: 4px !important;
  }
  
  .v-card {
    padding: 12px !important;
  }
  
  .v-icon {
    font-size: 24px !important;
  }
  
  .text-h4 {
    font-size: 1.5rem !important;
  }
  
  .text-subtitle-2 {
    font-size: 0.75rem !important;
  }
}
</style>